<template>
  <component
    :is="href ? (href.includes('/search') ? 'a' : LinkWrapper) : 'div'"
    v-if="showCommunityLink(href)"
    :class="[
      'root',
      depth === 1 && open ? 'root-folder-gap mb-4' : '',
      depth === 1 && !items ? 'no-hover-decoration' : '',
    ]"
    v-bind="{ href }"
    @click="onItemToggle"
    @keydown.enter="onItemToggle"
    @keydown.space="onItemToggle"
  >
    <div
      :tabindex="href ? null : 0"
      :class="['item flex flex-row items-center grow m-0 p-3 pointer rounded-md', { active }]"
      @keydown.tab="onTabPressed"
    >
      <v-icon v-if="icon" :name="icon" :class="['pr-2', { 'active-color': active }]" />
      <span
        :class="[
          'text text-sm font-extralight',
          appearance,
          depth === 1 && !items ? `depth-2` : `depth-${depth}`,
          { 'active-color': active },
        ]"
      >
        {{ label }}
      </span>
      <v-badge
        v-if="tag"
        :text="tag"
        connotation="information"
        :appearance="isVonageBrand ? 'subtle' : 'duotone'"
        shape="pill"
        class="inline-block my-0 mr-0 ml-1"
      />
      <div class="flex-grow" />
      <v-icon
        v-if="items && depth !== 1"
        data-cy="level-open-close-icon"
        class="arrow-icon"
        :name="open ? 'chevron-up-line' : 'chevron-down-line'"
        :size="-6"
      />
    </div>

    <div v-if="items" class="menu-container pl-2">
      <menu-item-group v-if="depth === 1" :items="items" :open="true" :depth="depth" data-cy="menu-items-depth-1" />
      <menu-item-group v-else :items="items" :open="open" :depth="depth" data-cy="menu-items-depth-not-1" />
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { SidebarNode } from '@bifrost-dev-docs-mfe/common/types/sidebar';
import { VIcon, VBadge } from '@vonage/vivid-vue';
import { LinkWrapper } from '@/components/markdown/widgets';
import MenuItemGroup from '@/components/navigation/left-sidebar/MenuItemGroup.vue';
import { isVonageBrand } from '@/constants';
import { showCommunityLink } from '@/utils/url';

const props = defineProps({
  items: { type: Array as PropType<SidebarNode[]>, required: false },
  label: { type: String, required: true },
  icon: { type: String, required: false },
  depth: { type: Number, required: true },
  href: { type: String, required: false },
  appearance: { type: String, required: false },
  open: { type: Boolean, required: true },
  tag: { type: String, required: false },
});

const active = computed(() => props.open && !props.items);

const emit = defineEmits(['toggled']);

const onItemToggle = (event: MouseEvent) => {
  if (!props.href && props.depth !== 1) {
    event.stopPropagation();
    emit('toggled', !props.open);
  }
};

const onTabPressed = (event: MouseEvent) => {
  if (!props.href && props.depth !== 1 && !props.open) {
    event.stopPropagation();
    emit('toggled', !props.open);
  }
};
</script>

<style lang="scss" scoped>
.root {
  a {
    color: var(--dp-left-side-bar-anchor-color) !important;
    margin: 0;

    &:hover {
      text-decoration: none !important;
    }
  }

  .item {
    &:hover {
      background-color: #f2f2f2;
    }

    &.active {
      background-color: var(--dp-left-side-bar-item-active-bg-color);
      padding: 10px;
    }

    .active-color {
      color: var(--dp-left-side-bar-item-active-color);
    }

    .text {
      &[class*='depth-'] {
        color: var(--dp-left-side-bar-item-depth-common-color);
        font: var(--dp-left-side-bar-item-depth-common-font);
      }

      &.depth-1 {
        text-transform: uppercase;
        font: var(--dp-left-side-bar-item-depth-1-font);
        letter-spacing: var(--dp-left-side-bar-item-depth-1-font-letter-spacing);
        color: var(--dp-left-side-bar-item-depth-1-color);
      }

      &.depth-2 {
        color: var(--dp-left-side-bar-item-depth-2-color);
        font: var(--dp-left-side-bar-item-depth-2-font);
      }

      &.active-color {
        color: var(--dp-left-side-bar-item-active-color);
      }

      &.link,
      &.highlighted {
        color: var(--dp-left-side-bar-item-link);
      }
    }

    .tag {
      background: var(--vvd-color-information-100);
      color: var(--vvd-color-information-800);
    }
  }
}
.no-hover-decoration {
  &:hover {
    text-decoration: none !important;
  }
}
</style>
